import React, { useState, useEffect, useCallback, useRef } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import {
 Button, CheckBoxField, ActionSelectV2, useToast, BarLoader,
} from '@unite-us/app-components';
import StatusSelect from 'src/components/StatusSelect';
import ClientProfileLink from 'src/components/ClientSummary/components/ClientProfileLink';
import { browserHistory } from 'common/utils/browserHistory';
import { fetchClientRelationship, updateClientRelationship } from 'actions/CareCoordinator/Contact/Group';
import { isEmpty, get } from 'lodash';
import {
  uploadAndAttachContactDocuments as uploadAndAttachContactDocumentsAction,
  fetchContactDocuments,
} from 'actions/Document/Contact/Group';
import UploadAndAttachDocuments from 'src/common/form/DocumentUploader/components/UploadAndAttachDocuments';
import { hasFeatureRole } from 'common/utils/Roles/hasFeatureRole';

const DrawerHeader = ({
  contact,
  fullContact,
  isFetchingFull,
  groupId,
  onClientNameClick,
  uploadAndAttachContactDocuments,
  fetchContactDocuments: fetchContactDocumentsAction,
  hasSocialCareNavigationRole,
}) => {
  const { addToast } = useToast();
  const [clientRelationship, setClientRelationship] = useState({});
  const [isLoadingRelationship, setIsLoadingRelationship] = useState(false);
  const [isUpdatingRelationship, setIsUpdatingRelationship] = useState(false);
  const clientDataRef = useRef(null);
  const full_name = contact?.full_name ? contact.full_name : `${contact?.first_name} ${contact?.last_name}`;
  const uploadDocsRef = useRef(null);
  const actionSelectRef = useRef(null);

  useEffect(() => {
    if (contact) {
      clientDataRef.current = contact;
    }
  }, [contact]);

  const resetActionSelect = useCallback(() => {
    if (actionSelectRef.current && actionSelectRef.current.resetValue) {
      actionSelectRef.current.resetValue();
    }
  }, []);

  const handleFindResources = useCallback(() => {
    const currentClientData = clientDataRef.current;

    if (!currentClientData?.id) {
      window.datadogLogs.logger.warn('Cannot find resources: client ID is not available.', currentClientData);
      return;
    }
    browserHistory.push(`/referrals/create/add-resources?person=${currentClientData.id}`);
    resetActionSelect();
  }, []);

  const getClientRelationship = async () => {
    try {
      setIsLoadingRelationship(true);
      const relationship = await fetchClientRelationship(contact.id, groupId);
      setClientRelationship(relationship);
    } catch (error) {
      window.datadogLogs.logger.error('Error fetching client relationship:', error);
    } finally {
      setIsLoadingRelationship(false);
    }
  };

  const handleOnCheck = async (e) => {
    try {
      setIsUpdatingRelationship(true);
      const relationship = await updateClientRelationship(clientRelationship, !!e.target.checked);
      setClientRelationship(relationship);
    } catch (error) {
      window.datadogLogs.logger.error('Error updating client relationship:', error);
    } finally {
      setIsUpdatingRelationship(false);
    }
  };

  const handleClientNameClick = () => {
    if (onClientNameClick) {
      onClientNameClick();
    } else {
      browserHistory.push(`/facesheet/${contact?.id}`);
    }
  };

  const openUploadDocuments = () => {
    if (uploadDocsRef.current) {
      uploadDocsRef.current.openDialog();
    }
  };

  const handleModalClose = useCallback(() => {
    // Reset the ActionSelect when the modal closes, regardless of the action
    resetActionSelect();
  }, [resetActionSelect]);

  const handleAttachDocuments = ({ uploadedDocuments, privateToOrg, privateToRole }) => {
    if (!isEmpty(uploadedDocuments)) {
      const additionalData = [
        {
          key: 'privateToOrg',
          value: privateToOrg,
        },
        {
          key: 'privateToRole',
          value: privateToRole,
        },
      ];

      uploadAndAttachContactDocuments({
        contactId: contact.id,
        uploadedDocuments,
        additionalData,
      }).then((response) => {
        // Check if the response contains an error
        if (response?.response?.data?.error) {
          throw new Error(response.response.data.error);
        }

        // Refresh the documents in Redux store after successful upload
        fetchContactDocumentsAction({ contactId: contact.id, groupId });

        const message = (
          <div>
            Documents uploaded.
          </div>
        );
        addToast({
          message,
          type: 'success',
        });
      }).catch((error) => {
        window.datadogLogs.logger.error('Error uploading documents:', error);
        addToast({
          message: 'Failed to upload document(s). Please try again.',
          type: 'error',
        });
      });
    }
  };

  useEffect(() => {
    if (contact?.id && groupId) {
      getClientRelationship();
    }
  }, [contact?.id, groupId]);

  const initCaseCreation = () => {
    const currentClientData = clientDataRef.current;

    browserHistory.push(`/contacts/${currentClientData.id}/cases/new/add-case-details`);
    resetActionSelect();
  };

  return (
    <div className="drawer-header mb-4">
      <div className="flex justify-between items-start">
        <div className="flex flex-col flex-1">
          <div className="flex items-center">
            <ClientProfileLink linkToCopy={`${window.location.origin}/facesheet/${contact?.id}`}>
              <Button
                className="hover:underline text-action-blue px-0 mr-0 font-bold"
                style={{ fontSize: '20px', fontFamily: 'Proxima Nova, sans-serif' }}
                link
                onClick={handleClientNameClick}
                label={full_name}
              />
            </ClientProfileLink>
          </div>

          <div className="flex mb-2">
            {fullContact && !isFetchingFull ?
              <StatusSelect contact={fullContact} isFetching={isFetchingFull} showCondensedView /> : (
                <div style={{ width: '176px' }}>
                  <BarLoader />
                </div>
            )}
          </div>

          {!isEmpty(clientRelationship) && (
            <div className="flex items-center">
              {isLoadingRelationship ? (
                <div style={{ width: '176px' }}>
                  <BarLoader />
                </div>
            ) : (
              <>
                <CheckBoxField
                  id="client-requires-action__checkbox"
                  className="client-requires-action__checkbox"
                  label="Client requires action"
                  checked={clientRelationship.flagged || false}
                  onChange={handleOnCheck}
                  disabled={isUpdatingRelationship}
                  showError={false}
                  showHint={false}
                />
                {isUpdatingRelationship && <div className="ml-2"><BarLoader /></div>}
              </>
              )}
            </div>
          )}
        </div>
        <div style={{ width: '176px' }}>
          <ActionSelectV2
            ref={actionSelectRef}
            actions={[
              {
                action: handleFindResources,
                description: 'Share resources with your client or send referrals through Unite Us.',
                icon: 'Search',
                label: `Find Resources for ${contact?.first_name || 'this client'}`,
                value: 'find_resources',
              },
              {
                action: initCaseCreation,
                description: 'Open an internal case for your client.',
                icon: 'Email',
                label: 'Create New Case',
                value: 'new_case',
              },
              {
                action: openUploadDocuments,
                description: "Upload a document to the client's face sheet.",
                icon: 'FolderOpen',
                label: 'Upload a Document',
                value: 'upload_document',
              },
            ]}
            id="action-with-icons-and-helper-text"
            label="Action Select"
            placeholder="Take Action"
          />
        </div>
      </div>
      <UploadAndAttachDocuments
        ref={uploadDocsRef}
        dropzoneName="client-documents"
        onAttachDocuments={handleAttachDocuments}
        onModalClose={handleModalClose}
        contactDocuments={[]}
        attachedDocuments={[]}
        shouldPortal
        showDocumentVisibility
        hasSocialCareNavigationRole={hasSocialCareNavigationRole}
        warningText={
          'Reminder: Unless you limit document visibility, all providers working with a client can access ' +
          'documents uploaded here. To upload documents specific to a referral or case (especially those ' +
          'with PHI), go to that referral/case record.'
        }
        modalTitle="Upload documents to face sheet"
      />
    </div>
  );
};

DrawerHeader.propTypes = {
  contact: PropTypes.shape({
    id: PropTypes.string.isRequired,
    full_name: PropTypes.string.isRequired,
    first_name: PropTypes.string.isRequired,
    last_name: PropTypes.string.isRequired,
  }).isRequired,
  fullContact: PropTypes.object,
  isFetchingFull: PropTypes.bool.isRequired,
  groupId: PropTypes.string.isRequired,
  onClientNameClick: PropTypes.func,
  uploadAndAttachContactDocuments: PropTypes.func.isRequired,
  fetchContactDocuments: PropTypes.func.isRequired,
  hasSocialCareNavigationRole: PropTypes.bool,
};

DrawerHeader.defaultProps = {
  fullContact: null,
  onClientNameClick: null,
  hasSocialCareNavigationRole: false,
};

// Use connect to map the action creator to props
const mapStateToProps = (state) => ({
  hasSocialCareNavigationRole: hasFeatureRole({
    employeeRoles: get(state, 'globalState.currentEmployee.roles', []),
    targetRoleKey: 'social_care_navigation',
  }),
});

export default connect(
  mapStateToProps,
  {
    uploadAndAttachContactDocuments: uploadAndAttachContactDocumentsAction,
    fetchContactDocuments,
  },
)(DrawerHeader);
